/* eslint-disable */
/** Internal type. DO NOT USE DIRECTLY. */
type Exact<T extends { [key: string]: unknown }> = { [K in keyof T]: T[K] };
/** Internal type. DO NOT USE DIRECTLY. */
export type Incremental<T> =
  | T
  | {
      [P in keyof T]?: P extends " $fragmentName" | "__typename" ? T[P] : never;
    };
import type { TypedDocumentNode as DocumentNode } from "@graphql-typed-document-node/core";
export type DelegationReason =
  "CUSTOMER_REQUEST" | "PERIODIC_REVIEW" | "SETUP_SUPPORT" | "TROUBLESHOOTING";

/** どの段まで起こしたか。段は進むだけで戻らない。 */
export type ProductSemanticsFillStage =
  /** 商品画像の取り込みジョブだけを起こした */
  | "IMAGE_FETCH"
  /** 取り込みが済んだので DLT パイプラインも起こした */
  | "SEMANTICS_PIPELINE";

/**
 * 実行の状態。**`UNKNOWN` を成功へ倒さない。**
 *
 * Databricks へ届かない・語彙に無い値が返った・そもそも問い合わせる口が
 * まだ無い(パイプラインの更新)ときは `UNKNOWN` になる。
 * 画面はそれを「状態を確認できません」と出す。
 */
export type ProductSemanticsFillState =
  "FAILED" | "RUNNING" | "SUCCEEDED" | "UNKNOWN";

export type StartDelegationInput = {
  /** 閉じた語彙から必須で 1 つ。理由なしの代行を作らせない。 */
  reason: DelegationReason;
  /** 自由記述(任意)。監査には伏せ字のライタを通してから載る。 */
  reasonNote?: string | null | undefined;
  /** どの会社に入るか。 */
  tenantId: string | number;
};

export type StartProductSemanticsFillInput = {
  /**
   * 1 回に取り込む商品画像の件数。**上限はサーバー側の定数(30)**で、
   * 引数で広げられない。範囲外は `BAD_USER_INPUT`。
   */
  limit: number;
  /**
   * 取り込む商品を**この束に固定する**。省略・空なら従来どおり、ジョブ側の
   * 既定の選び方(レビュー件数の多い順)に任せる。
   *
   * 件数は `limit` 以下であること(ジョブ側が同じ条件で弾く)。重複は落とし、
   * ID の形が不正なら `BAD_USER_INPUT`。**渡した ID が承認済みの上位 200 商品に
   * 含まれていなければ、ジョブ側が実行ごと落とす**(ここを通っても保証にはならない)。
   */
  productIds?: Array<string | number> | null | undefined;
  /** どの会社に対して起こすか。 */
  tenantId: string | number;
};

export type StartProductSemanticsPipelineInput = {
  /** 1 段目の起動の id(`ProductSemanticsFill.id`)。 */
  fillId: string | number;
};

export type StartDelegationMutationVariables = Exact<{
  input: StartDelegationInput;
}>;

export type StartDelegationMutation = {
  operator: {
    startDelegation: {
      accessToken: string | null;
      expiresIn: number | null;
      delegation: { id: string; expiresAt: string; actorLabel: string } | null;
      userErrors: Array<{ code: string; message: string }>;
    };
  };
};

export type OperatorProductSemanticsFillsQueryVariables = Exact<{
  tenantId: string | number;
  first?: number | null | undefined;
}>;

export type OperatorProductSemanticsFillsQuery = {
  operator: {
    productSemanticsFills: Array<{
      id: string;
      tenantId: string;
      requestedLimit: number;
      stage: ProductSemanticsFillStage;
      state: ProductSemanticsFillState;
      startedAt: string;
      startedByLabel: string;
      jobRunId: string | null;
      pipelineUpdateId: string | null;
      finishedAt: string | null;
      counts: {
        targets: number;
        fetched: number;
        skippedExisting: number;
        failed: number;
        failureReasons: Array<string>;
      } | null;
    }>;
  };
};

export type OperatorProductSemanticsFillAvailabilityQueryVariables = Exact<{
  [key: string]: never;
}>;

export type OperatorProductSemanticsFillAvailabilityQuery = {
  operator: {
    productSemanticsFillAvailability: {
      imageFetchEnabled: boolean;
      pipelineEnabled: boolean;
    };
  };
};

export type OperatorStartProductSemanticsFillMutationVariables = Exact<{
  input: StartProductSemanticsFillInput;
}>;

export type OperatorStartProductSemanticsFillMutation = {
  operator: {
    startProductSemanticsFill: {
      fill: { id: string } | null;
      userErrors: Array<{ code: string; message: string }>;
    };
  };
};

export type OperatorStartProductSemanticsPipelineMutationVariables = Exact<{
  input: StartProductSemanticsPipelineInput;
}>;

export type OperatorStartProductSemanticsPipelineMutation = {
  operator: {
    startProductSemanticsPipeline: {
      fill: { id: string } | null;
      userErrors: Array<{ code: string; message: string }>;
    };
  };
};

export type OperatorTenantSummariesQueryVariables = Exact<{
  first?: number | null | undefined;
  after?: string | null | undefined;
}>;

export type OperatorTenantSummariesQuery = {
  operator: {
    tenantSummaries: {
      totalCount: number | null;
      pageInfo: { hasNextPage: boolean; endCursor: string | null };
      edges: Array<{
        cursor: string;
        node: {
          coverage: number | null;
          rulesRunning: number | null;
          coverageComputedAt: string | null;
          tenant: { id: string; label: string; createdAt: string };
        };
      }>;
    };
  };
};

export const StartDelegationDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "mutation",
      name: { kind: "Name", value: "StartDelegation" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "input" },
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "StartDelegationInput" },
            },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "operator" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "startDelegation" },
                  arguments: [
                    {
                      kind: "Argument",
                      name: { kind: "Name", value: "input" },
                      value: {
                        kind: "Variable",
                        name: { kind: "Name", value: "input" },
                      },
                    },
                  ],
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "delegation" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "id" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "expiresAt" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "actorLabel" },
                            },
                          ],
                        },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "accessToken" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "expiresIn" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "userErrors" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "code" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "message" },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  StartDelegationMutation,
  StartDelegationMutationVariables
>;
export const OperatorProductSemanticsFillsDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "OperatorProductSemanticsFills" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "tenantId" },
          },
          type: {
            kind: "NonNullType",
            type: { kind: "NamedType", name: { kind: "Name", value: "ID" } },
          },
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "first" },
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "operator" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "productSemanticsFills" },
                  arguments: [
                    {
                      kind: "Argument",
                      name: { kind: "Name", value: "tenantId" },
                      value: {
                        kind: "Variable",
                        name: { kind: "Name", value: "tenantId" },
                      },
                    },
                    {
                      kind: "Argument",
                      name: { kind: "Name", value: "first" },
                      value: {
                        kind: "Variable",
                        name: { kind: "Name", value: "first" },
                      },
                    },
                  ],
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "tenantId" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "requestedLimit" },
                      },
                      { kind: "Field", name: { kind: "Name", value: "stage" } },
                      { kind: "Field", name: { kind: "Name", value: "state" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "startedAt" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "startedByLabel" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "jobRunId" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "pipelineUpdateId" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "finishedAt" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "counts" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "targets" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "fetched" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "skippedExisting" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "failed" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "failureReasons" },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  OperatorProductSemanticsFillsQuery,
  OperatorProductSemanticsFillsQueryVariables
>;
export const OperatorProductSemanticsFillAvailabilityDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "OperatorProductSemanticsFillAvailability" },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "operator" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: {
                    kind: "Name",
                    value: "productSemanticsFillAvailability",
                  },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "imageFetchEnabled" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "pipelineEnabled" },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  OperatorProductSemanticsFillAvailabilityQuery,
  OperatorProductSemanticsFillAvailabilityQueryVariables
>;
export const OperatorStartProductSemanticsFillDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "mutation",
      name: { kind: "Name", value: "OperatorStartProductSemanticsFill" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "input" },
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "StartProductSemanticsFillInput" },
            },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "operator" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "startProductSemanticsFill" },
                  arguments: [
                    {
                      kind: "Argument",
                      name: { kind: "Name", value: "input" },
                      value: {
                        kind: "Variable",
                        name: { kind: "Name", value: "input" },
                      },
                    },
                  ],
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "fill" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "id" },
                            },
                          ],
                        },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "userErrors" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "code" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "message" },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  OperatorStartProductSemanticsFillMutation,
  OperatorStartProductSemanticsFillMutationVariables
>;
export const OperatorStartProductSemanticsPipelineDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "mutation",
      name: { kind: "Name", value: "OperatorStartProductSemanticsPipeline" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "input" },
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: {
                kind: "Name",
                value: "StartProductSemanticsPipelineInput",
              },
            },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "operator" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: {
                    kind: "Name",
                    value: "startProductSemanticsPipeline",
                  },
                  arguments: [
                    {
                      kind: "Argument",
                      name: { kind: "Name", value: "input" },
                      value: {
                        kind: "Variable",
                        name: { kind: "Name", value: "input" },
                      },
                    },
                  ],
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "fill" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "id" },
                            },
                          ],
                        },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "userErrors" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "code" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "message" },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  OperatorStartProductSemanticsPipelineMutation,
  OperatorStartProductSemanticsPipelineMutationVariables
>;
export const OperatorTenantSummariesDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "OperatorTenantSummaries" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "first" },
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } },
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "after" },
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Cursor" } },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "operator" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "tenantSummaries" },
                  arguments: [
                    {
                      kind: "Argument",
                      name: { kind: "Name", value: "first" },
                      value: {
                        kind: "Variable",
                        name: { kind: "Name", value: "first" },
                      },
                    },
                    {
                      kind: "Argument",
                      name: { kind: "Name", value: "after" },
                      value: {
                        kind: "Variable",
                        name: { kind: "Name", value: "after" },
                      },
                    },
                  ],
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "totalCount" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "pageInfo" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "hasNextPage" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "endCursor" },
                            },
                          ],
                        },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "edges" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "cursor" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "node" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "tenant" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: { kind: "Name", value: "id" },
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "label",
                                          },
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "createdAt",
                                          },
                                        },
                                      ],
                                    },
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "coverage" },
                                  },
                                  {
                                    kind: "Field",
                                    name: {
                                      kind: "Name",
                                      value: "rulesRunning",
                                    },
                                  },
                                  {
                                    kind: "Field",
                                    name: {
                                      kind: "Name",
                                      value: "coverageComputedAt",
                                    },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  OperatorTenantSummariesQuery,
  OperatorTenantSummariesQueryVariables
>;
